import { useState, useCallback, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

/**
 * Tour hook for Resume List page
 * Shows key features of the resume management interface
 */
export const useResumeListTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const getTourSteps = (): TourStep[] => [
        {
            selector: '[data-tour="resume-list"]',
            title: 'Quản lý hồ sơ ứng viên',
            description:
                'Trang quản lý toàn bộ hồ sơ ứng tuyển.\n• Xem danh sách ứng viên\n• Sàng lọc hồ sơ\n• Theo dõi trạng thái phỏng vấn\n• Xuất báo cáo',
        },
        {
            selector: '[data-tour="create-resume-btn"]',
            title: 'Thêm hồ sơ mới',
            description:
                'Click để thêm hồ sơ ứng viên mới:\n• Nhập thông tin cá nhân\n• Chọn vị trí ứng tuyển\n• Upload CV\n• Ghi chú đánh giá',
        },
        {
            selector: '[data-tour="manage-jobs-btn"]',
            title: 'Quản lý tin tuyển dụng',
            description:
                'Truy cập quản lý tin tuyển dụng:\n• Tạo tin tuyển dụng mới\n• Chỉnh sửa vị trí\n• Đóng/mở tin tuyển dụng\n• Xem số lượng ứng viên',
        },
        {
            selector: '[data-tour="resume-filter-bar"]',
            title: 'Bộ lọc hồ sơ',
            description:
                'Lọc hồ sơ theo nhiều tiêu chí:\n• Vị trí ứng tuyển\n• Nguồn CV (LinkedIn, Website, ...)\n• Phòng ban\n• Thời gian nộp CV',
        },
        {
            selector: '[data-tour="resume-search"]',
            title: 'Tìm kiếm nhanh',
            description:
                'Tìm kiếm hồ sơ theo:\n• Tên ứng viên\n• Email\n• Số điện thoại\n• Vị trí ứng tuyển',
        },
        {
            selector: '[data-tour="resume-data-table"]',
            title: 'Bảng danh sách hồ sơ',
            description:
                'Bảng hiển thị thông tin ứng viên:\n• Click ID/Tên để xem chi tiết\n• Sắp xếp theo cột (ID, Tên, Email, ...)\n• Thao tác: Chỉnh sửa, Xoá\n• Xem CV trực tuyến',
        },
        {
            selector: '[data-tour="resume-settings"]',
            title: 'Cài đặt và Export',
            description:
                'Tuỳ chỉnh hiển thị và xuất dữ liệu:\n• Cỡ chữ\n• Chiều cao hàng\n• Ẩn/hiện cột\n• Export Excel danh sách hồ sơ',
        },
    ]

    const getVisibleSteps = useCallback(() => {
        return getTourSteps().filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStep(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () => goToStep(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => goToStep(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    const goToStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            setCurrentStep(stepIndex)
            showTourStep(stepIndex, steps)
        },
        [showTourStep]
    )

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}

export default useResumeListTour
